import type { Tower } from "../entities/Tower.ts";
import type {
  SynergyDefinition,
  ActiveSynergy,
  Element,
} from "../entities/types.ts";
import { SYNERGY_PALETTE } from "../core/palette.ts";

/**
 * Pure functions for detecting synergy pairs and tri-combos
 * between adjacent towers on the grid.
 */

// RESONANCE identity colour — born in SYNERGY_PALETTE like every other
// synergy's (BL-111); the entry is guaranteed by the palette contract test.
const RESONANCE_PALETTE = SYNERGY_PALETTE.resonance!;

export function getNeighborTowers(
  tower: Tower,
  towers: Tower[],
  allowDiagonal: boolean,
): Tower[] {
  const neighbors: Tower[] = [];
  const offsets = allowDiagonal
    ? [
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
        [-1, -1],
        [-1, 1],
        [1, -1],
        [1, 1],
      ]
    : [
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
      ];

  for (const offset of offsets) {
    const nc = tower.col + offset[0]!;
    const nr = tower.row + offset[1]!;
    for (const t of towers) {
      if (t.col === nc && t.row === nr) neighbors.push(t);
    }
  }
  return neighbors;
}

export function findSynergy(
  elements: Element[],
  synergyData: SynergyDefinition[],
): SynergyDefinition | null {
  const sorted = [...elements].sort();
  for (const syn of synergyData) {
    if (syn.elements.length !== sorted.length) continue;
    const synSorted = [...syn.elements].sort();
    if (synSorted.every((e, idx) => e === sorted[idx])) return { ...syn };
  }
  return null;
}

export function getPairId(a: Tower, b: Tower): string {
  const id1 = `${a.col},${a.row}`;
  const id2 = `${b.col},${b.row}`;
  return id1 < id2 ? `${id1}-${id2}` : `${id2}-${id1}`;
}

export function hasPair(
  a: Tower,
  b: Tower,
  activeSynergies: ActiveSynergy[],
): boolean {
  const pairId = getPairId(a, b);
  for (const syn of activeSynergies) {
    if (syn.towers.length === 2) {
      const t0 = syn.towers[0] as Tower;
      const t1 = syn.towers[1] as Tower;
      if (getPairId(t0, t1) === pairId) return true;
    }
  }
  return false;
}

export function hasTriSynergy(
  towers: Tower[],
  activeSynergies: ActiveSynergy[],
): boolean {
  for (const syn of activeSynergies) {
    if (syn.towers.length !== 3) continue;
    if (towers.every((t) => syn.towers.includes(t))) return true;
  }
  return false;
}

export interface DetectionContext {
  towers: Tower[];
  synergyData: SynergyDefinition[];
  allowDiagonal: boolean;
  resonanceEnabled?: boolean;
  /** MONOCULTURE (BL-72): towers count as element-agnostic, so any 3+ adjacent
   *  cluster forms a RESONANCE synergy regardless of element — a bounded,
   *  deterministic reading of "all towers count as all elements". */
  monoculture?: boolean;
}

/** Detect all active synergies among the given towers. */
export function detectSynergies(ctx: DetectionContext): ActiveSynergy[] {
  const { towers, synergyData, allowDiagonal } = ctx;
  const activeSynergies: ActiveSynergy[] = [];

  for (const tower of towers) {
    const neighbors = getNeighborTowers(tower, towers, allowDiagonal);
    for (const neighbor of neighbors) {
      // Same-element pairs intentionally form no pair-synergy (F-15, D-091).
      // Same-element clustering is a *coverage* axis, not a power axis: its
      // payoff tier is RESONANCE (3+ adjacent), bounded above by the CR-17
      // anti-cheese damage penalty (0.85^n at 4+). A pair-level damage reward
      // would undercut that penalty and blur cross=power / same=coverage.
      if (tower.element === neighbor.element) continue;
      if (hasPair(tower, neighbor, activeSynergies)) continue;

      const synergy = findSynergy(
        [tower.element, neighbor.element],
        synergyData,
      );
      if (synergy) {
        activeSynergies.push({
          ...synergy,
          towers: [tower, neighbor],
          centerX: (tower.x + neighbor.x) / 2,
          centerY: (tower.y + neighbor.y) / 2,
        });
      }
    }
  }

  // Check tri-synergies
  for (const tower of towers) {
    const neighbors = getNeighborTowers(tower, towers, allowDiagonal);
    for (let j = 0; j < neighbors.length; j++) {
      for (let k = j + 1; k < neighbors.length; k++) {
        const nj = neighbors[j]!;
        const nk = neighbors[k]!;
        const elements = new Set([tower.element, nj.element, nk.element]);
        if (elements.size !== 3) continue;

        const n2Neighbors = getNeighborTowers(nj, towers, allowDiagonal);
        const connected = n2Neighbors.includes(nk);
        if (!connected) continue;

        const triTowers: Tower[] = [tower, nj, nk];
        if (hasTriSynergy(triTowers, activeSynergies)) continue;

        const synergy = findSynergy([...elements] as Element[], synergyData);
        if (synergy) {
          const cx = (triTowers[0]!.x + triTowers[1]!.x + triTowers[2]!.x) / 3;
          const cy = (triTowers[0]!.y + triTowers[1]!.y + triTowers[2]!.y) / 3;
          activeSynergies.push({
            ...synergy,
            towers: triTowers,
            centerX: cx,
            centerY: cy,
          });
        }
      }
    }
  }

  // RESONANCE synergy: any 3+ adjacent towers regardless of type. MONOCULTURE
  // makes towers element-agnostic, which is precisely this clustering, so it
  // enables the same detection path (its payoff offsets the −25% damage tax).
  if (ctx.resonanceEnabled || ctx.monoculture) {
    detectResonance(towers, allowDiagonal, activeSynergies);
  }

  return activeSynergies;
}

/** Detect RESONANCE groups: 3+ adjacent towers of any type. */
function detectResonance(
  towers: Tower[],
  allowDiagonal: boolean,
  activeSynergies: ActiveSynergy[],
): void {
  // Find connected clusters of 3+ towers
  const visited = new Set<Tower>();
  for (const tower of towers) {
    if (visited.has(tower)) continue;
    const cluster = floodFillCluster(tower, towers, allowDiagonal, visited);
    if (cluster.length >= 3) {
      const cx = cluster.reduce((s, t) => s + t.x, 0) / cluster.length;
      const cy = cluster.reduce((s, t) => s + t.y, 0) / cluster.length;
      activeSynergies.push({
        id: "resonance",
        name: "RESONANCE",
        elements: [],
        description: "AoE pulse from adjacent tower cluster",
        color: RESONANCE_PALETTE.color,
        colorLight: RESONANCE_PALETTE.colorLight,
        effect: {
          type: "aoe_pulse",
          damage: 10,
          radius: 2,
          tickInterval: 3000,
        },
        towers: cluster,
        centerX: cx,
        centerY: cy,
      });
    }
  }
}

function floodFillCluster(
  start: Tower,
  allTowers: Tower[],
  allowDiagonal: boolean,
  visited: Set<Tower>,
): Tower[] {
  const cluster: Tower[] = [];
  const queue: Tower[] = [start];
  visited.add(start);
  while (queue.length > 0) {
    const current = queue.pop()!;
    cluster.push(current);
    const neighbors = getNeighborTowers(current, allTowers, allowDiagonal);
    for (const n of neighbors) {
      if (!visited.has(n)) {
        visited.add(n);
        queue.push(n);
      }
    }
  }
  return cluster;
}
